import { useMemo, useState, type Key } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TrashIcon } from '@phosphor-icons/react';
import {
  useCreditScores,
  type CreditScoreEntry,
  type Person,
} from '../data/useCreditScores';
import { useHousehold } from '../auth/useHousehold';
import { useScope } from '../hooks/useScope';
import { getPersonName } from '../config/personNames';
import { CreditScoreModal } from './CreditScoreModal';
import { CreditScoreDonut } from './CreditScoreDonut';
import { CardHeader } from './CardHeader';

const SERIES_COLOR: Record<Person, string> = {
  dakota: 'var(--berg-dakota)',
  natalia: 'var(--berg-natalia)',
};

// Mirrors the .person-dot + .dot-glow utility in SVG land — box-shadow
// doesn't apply to <circle>, so we render the same dot beneath a blurred
// duplicate via an feGaussianBlur filter. The blurred copy gets a 32%
// fill-opacity tint matching the CSS dot-glow's two-shadow alpha.
const GLOW_FILTER_ID = 'credit-chart-dot-glow';

type RechartsDotProps = {
  cx?: number;
  cy?: number;
  // Recharts' typings type `key` as `Key | null | undefined` (where Key
  // includes bigint); accept the wider shape so our renderer slots into
  // both `dot` and `activeDot` props.
  key?: Key | null;
};

function makePersonDot(color: string) {
  return (props: RechartsDotProps) => {
    const { cx, cy, key } = props;
    if (cx == null || cy == null) return <g key={key} />;
    return (
      <g key={key}>
        {/* Soft halo. The filter spreads the circle outward as a true
            Gaussian blur — same look as the CSS box-shadow on .dot-glow. */}
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={color}
          fillOpacity={0.32}
          filter={`url(#${GLOW_FILTER_ID})`}
        />
        {/* Sharp core. Radius matches the .person-dot CSS (0.5rem = 4px
            radius approximation) — a hair narrower so the halo peeks out. */}
        <circle cx={cx} cy={cy} r={3.5} fill={color} />
      </g>
    );
  };
}

function makePersonActiveDot(color: string) {
  return (props: RechartsDotProps) => {
    const { cx, cy, key } = props;
    if (cx == null || cy == null) return <g key={key} />;
    return (
      <g key={key}>
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={color}
          fillOpacity={0.32}
          filter={`url(#${GLOW_FILTER_ID})`}
        />
        <circle cx={cx} cy={cy} r={5} fill={color} />
      </g>
    );
  };
}

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  padding: '8px 12px',
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};

// Date columns survive the wire as either YYYY-MM-DD (after the explicit
// ::text cast in _history.ts) or as a full ISO timestamp (legacy rows /
// unexpected driver behavior). Normalize both before parsing.
function parseObserved(raw: string): number {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00Z` : raw;
  return Date.parse(normalized);
}

function formatDate(iso: string): string {
  const t = parseObserved(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Generate evenly-spaced x-axis ticks across the data range. Step adapts
// to span: yearly for 3+ years, quarterly for 1–3 years, monthly under one
// year. Ticks land on month-1 boundaries (Jan 1 / Apr 1 / etc.) so labels
// are stable and don't fall on arbitrary entry days.
function buildTimeTicks(minTs: number, maxTs: number): number[] {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs <= minTs) {
    return [];
  }
  const min = new Date(minTs);
  const max = new Date(maxTs);
  const monthsSpan =
    (max.getUTCFullYear() - min.getUTCFullYear()) * 12 +
    (max.getUTCMonth() - min.getUTCMonth());
  const step = monthsSpan >= 36 ? 12 : monthsSpan >= 12 ? 3 : 1;

  // Align the first tick to the next step-aligned month at or after min.
  let y = min.getUTCFullYear();
  let m = Math.ceil(min.getUTCMonth() / step) * step;
  while (m >= 12) {
    y += 1;
    m -= 12;
  }

  const ticks: number[] = [];
  // eslint-disable-next-line no-constant-condition -- intentional: breaks when ts > maxTs
  while (true) {
    const ts = Date.UTC(y, m, 1);
    if (ts > maxTs) break;
    if (ts >= minTs) ticks.push(ts);
    m += step;
    while (m >= 12) {
      y += 1;
      m -= 12;
    }
  }
  return ticks;
}

function shortDateFromTs(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDateFromTs(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

interface ChartPoint {
  date: string;
  // Timestamp (ms, UTC midnight) used as the numeric x-value so the chart
  // spaces points by real elapsed time instead of evenly per category.
  ts: number;
  dakota?: number | null;
  natalia?: number | null;
}

function buildChartData(
  dakota: CreditScoreEntry[],
  natalia: CreditScoreEntry[]
): ChartPoint[] {
  // Merge both series on the union of observation dates. Multiple entries
  // for the same person on the same date collapse to the highest score
  // (the user's most recent observation per day is the intent).
  const byDate = new Map<string, ChartPoint>();
  const upsert = (date: string): ChartPoint => {
    const existing = byDate.get(date);
    if (existing) return existing;
    const fresh: ChartPoint = { date, ts: parseObserved(date) };
    byDate.set(date, fresh);
    return fresh;
  };
  for (const e of dakota) {
    const p = upsert(e.observedAt);
    p.dakota = Math.max(p.dakota ?? 0, e.score);
  }
  for (const e of natalia) {
    const p = upsert(e.observedAt);
    p.natalia = Math.max(p.natalia ?? 0, e.score);
  }
  return Array.from(byDate.values()).sort((a, b) => a.ts - b.ts);
}

interface RowProps {
  entry: CreditScoreEntry;
  person: Person;
  canDelete: boolean;
  onDelete: () => void;
}

function EntryRow({ entry, person, canDelete, onDelete }: RowProps) {
  return (
    <tr className="border-t border-border">
      <td className="py-2 pr-4 tabular text-fg-secondary">
        {formatDate(entry.observedAt)}
      </td>
      <td className="py-2 pr-4">
        <span className="inline-flex items-center gap-2">
          <span
            className={`person-dot ${person === 'dakota' ? 'dot-dakota' : 'dot-natalia'}`}
          />
          {getPersonName(person)}
        </span>
      </td>
      <td className="py-2 pr-4 tabular font-semibold">{entry.score}</td>
      <td className="py-2 pr-4 text-fg-secondary">{entry.model ?? '—'}</td>
      <td className="py-2 pl-2 text-right">
        {canDelete ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete entry"
            className="text-fg-tertiary hover:text-red transition-colors p-1"
          >
            <TrashIcon size={16} />
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function MobileEntryRow({ entry, person, canDelete, onDelete }: RowProps) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className={`person-dot ${person === 'dakota' ? 'dot-dakota' : 'dot-natalia'} shrink-0`}
      />
      <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
        <div className="min-w-0 flex flex-col">
          <span className="text-sm font-medium truncate">
            {getPersonName(person)}
          </span>
          <span className="text-xs text-fg-tertiary mt-0.5">
            {formatDate(entry.observedAt)}
          </span>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="tabular font-semibold">{entry.score}</span>
          {entry.model && (
            <span className="text-xs text-fg-tertiary mt-0.5">{entry.model}</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={canDelete ? onDelete : undefined}
        aria-label="Delete entry"
        aria-hidden={!canDelete}
        tabIndex={canDelete ? 0 : -1}
        className={`shrink-0 p-1 ${
          canDelete
            ? 'text-fg-tertiary hover:text-red transition-colors'
            : 'invisible pointer-events-none'
        }`}
      >
        <TrashIcon size={16} />
      </button>
    </div>
  );
}

export function CreditTab() {
  const credit = useCreditScores();
  const household = useHousehold();
  const { scope } = useScope();
  const [searchParams] = useSearchParams();
  const forceStale = searchParams.get('reminderDebug') === '1';
  const [modalOpen, setModalOpen] = useState(false);

  const add = credit.status === 'ready' ? credit.add : null;
  const remove = credit.status === 'ready' ? credit.remove : null;

  const selfPerson: Person | null =
    household.status === 'ready' && household.selfId != null
      ? household.ids.dakota === household.selfId
        ? 'dakota'
        : household.ids.natalia === household.selfId
          ? 'natalia'
          : null
      : null;

  const chartData = useMemo(
    () => buildChartData(credit.data.dakota, credit.data.natalia),
    [credit.data.dakota, credit.data.natalia]
  );

  const xAxisTicks = useMemo(() => {
    if (chartData.length === 0) return [];
    const minTs = chartData[0].ts;
    const maxTs = chartData[chartData.length - 1].ts;
    return buildTimeTicks(minTs, maxTs);
  }, [chartData]);

  // Combine + sort all entries newest-first for the table, tagging the owner
  // so a delete handler can scope the action. Filtered by the page-level
  // scope toggle so the History card respects whichever person tab the user
  // has selected at the top of the page.
  const tableRows = useMemo(() => {
    const all: { entry: CreditScoreEntry; person: Person }[] = [
      ...credit.data.dakota.map((e) => ({ entry: e, person: 'dakota' as const })),
      ...credit.data.natalia.map((e) => ({ entry: e, person: 'natalia' as const })),
    ];
    all.sort((a, b) =>
      a.entry.observedAt < b.entry.observedAt
        ? 1
        : a.entry.observedAt > b.entry.observedAt
          ? -1
          : Number(b.entry.id) - Number(a.entry.id)
    );
    if (scope === 'household') return all;
    return all.filter((r) => r.person === scope);
  }, [credit.data.dakota, credit.data.natalia, scope]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-fg-secondary">
          No automated pull — record what your bank or card issuer shows you, and
          the chart fills in over time.
        </p>
        {add && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="btn btn-secondary"
          >
            Add score
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
        <CreditScoreDonut
          person="dakota"
          entries={credit.data.dakota}
          isSelf={selfPerson === 'dakota'}
          forceStale={forceStale}
          onAddClick={() => setModalOpen(true)}
        />
        <CreditScoreDonut
          person="natalia"
          entries={credit.data.natalia}
          isSelf={selfPerson === 'natalia'}
          forceStale={forceStale}
          onAddClick={() => setModalOpen(true)}
        />
      </div>

      <div className="card">
        <CardHeader
          title="Score over time"
          subtitle={
            scope === 'household'
              ? 'One line per household member'
              : `${getPersonName(scope)}'s scores over time`
          }
        />
        <div className="h-72 -mx-2">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-fg-tertiary text-sm">
              No entries yet — add one to start the trend line.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 4, bottom: 0 }}
              >
                <defs>
                  {/* Gaussian blur filter for the dot halos. Bounding box
                      is expanded with negative x/y + 200%+ width/height so
                      the blur isn't clipped by the default filter region. */}
                  <filter
                    id={GLOW_FILTER_ID}
                    x="-100%"
                    y="-100%"
                    width="300%"
                    height="300%"
                  >
                    <feGaussianBlur stdDeviation="2.5" />
                  </filter>
                </defs>
                <CartesianGrid
                  stroke="var(--berg-border)"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  ticks={xAxisTicks}
                  interval={0}
                  tickFormatter={(v) => shortDateFromTs(Number(v))}
                  stroke="var(--berg-text-tertiary)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[450, 850]}
                  ticks={[450, 550, 650, 750, 850]}
                  stroke="var(--berg-text-tertiary)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(v) => formatDateFromTs(Number(v))}
                  formatter={(value, name) => {
                    const personKey = String(name) as Person;
                    return [value, getPersonName(personKey)];
                  }}
                />
                {(scope === 'household' || scope === 'dakota') && (
                  <Line
                    type="monotone"
                    dataKey="dakota"
                    name="dakota"
                    stroke={SERIES_COLOR.dakota}
                    strokeWidth={2}
                    dot={makePersonDot(SERIES_COLOR.dakota)}
                    activeDot={makePersonActiveDot(SERIES_COLOR.dakota)}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
                {(scope === 'household' || scope === 'natalia') && (
                  <Line
                    type="monotone"
                    dataKey="natalia"
                    name="natalia"
                    stroke={SERIES_COLOR.natalia}
                    strokeWidth={2}
                    dot={makePersonDot(SERIES_COLOR.natalia)}
                    activeDot={makePersonActiveDot(SERIES_COLOR.natalia)}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card">
        <CardHeader
          title="History"
          subtitle={
            scope === 'household'
              ? 'Every entry, newest first'
              : `${getPersonName(scope)}'s entries, newest first`
          }
        />
        {tableRows.length === 0 ? (
          <div className="py-8 text-center text-fg-tertiary text-sm">
            No entries to show.
          </div>
        ) : (
          <>
            {/* Desktop: traditional table. */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.06em] text-fg-tertiary">
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium">Person</th>
                    <th className="py-2 pr-4 font-medium">Score</th>
                    <th className="py-2 pr-4 font-medium">Model</th>
                    <th className="py-2 pl-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map(({ entry, person }) => (
                    <EntryRow
                      key={entry.id}
                      entry={entry}
                      person={person}
                      canDelete={Boolean(remove) && selfPerson === person}
                      onDelete={() => {
                        if (!remove) return;
                        if (
                          typeof window !== 'undefined' &&
                          !window.confirm('Delete this credit score entry?')
                        ) {
                          return;
                        }
                        void remove(entry.id);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile: stacked rows, same divide-y pattern as TransactionsPage. */}
            <div className="sm:hidden -mx-4 divide-y divide-border border-y border-border">
              {tableRows.map(({ entry, person }) => (
                <MobileEntryRow
                  key={entry.id}
                  entry={entry}
                  person={person}
                  canDelete={Boolean(remove) && selfPerson === person}
                  onDelete={() => {
                    if (!remove) return;
                    if (
                      typeof window !== 'undefined' &&
                      !window.confirm('Delete this credit score entry?')
                    ) {
                      return;
                    }
                    void remove(entry.id);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {modalOpen && add && (
        <CreditScoreModal
          personLabel={selfPerson ? getPersonName(selfPerson) : 'Your entry'}
          onSubmit={async (input) => {
            await add(input);
          }}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
